import { Alert, Box, Breadcrumbs, IconButton, Link, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import { socketUrl } from '../../../sockets/socketConfig';
import EspelhoList from './components/EspelhoList';
import TerminalsResume from './components/TerminalsResume';
import EspelhoFilter from './components/EspelhoFilter';

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    gap: 2,
});

const ReturnContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
});

const Header = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '1.2rem',
    fontWeight: '600',
}));

const EspelhosView = () => {
    const navigate = useNavigate();
    const [isConnected, setIsConnected] = useState(false);
    const [connection, setConnection] = useState(null);
    const [espelhos, setEspelhos] = useState([]);
    const espelhosRef = useRef();
    espelhosRef.current = espelhos;
    const [filteredEspelhos, setFilteredEspelhos] = useState([]);
    const [filters, setFilters] = useState({ label: '', connectionStatus: '' });

    const [terminals, setTerminals] = useState([]);
    const terminalsRef = useRef();
    terminalsRef.current = terminals;

    useEffect(() => {
        const connect = new HubConnectionBuilder()
            .withUrl(socketUrl)
            // .configureLogging(LogLevel.Trace)
            // .withHubProtocol(new MessagePackHubProtocol())
            .withAutomaticReconnect()
            .build();

        setConnection(connect);
    }, []);

    const sortEspelhoJsonByLabel = (data) => {
        return data.sort((a, b) => {
            if (a.Label === null && b.Label === null) return 0;
            else if (a.Label === null) return 1;
            else if (b.Label === null) return -1;
            else return a.Label.localeCompare(b.Label);
        });
    };

    const updateEspelho = (updatedEspelho) => {
        const newEspelhos = [...espelhosRef.current];
        const index = newEspelhos.findIndex((espelho) => espelho.Id === updatedEspelho.Id);
        if (index !== -1) {
            newEspelhos[index] = updatedEspelho;
        } else {
            newEspelhos.push(updatedEspelho);
        }
        setEspelhos(newEspelhos);
    };

    const updateTerminal = (updatedTerminal) => {
        const newTerminals = [...terminalsRef.current];
        const index = newTerminals.findIndex((t) => t.Id === updatedTerminal.Id);
        if (index !== -1) {
            newTerminals[index] = updatedTerminal;
        } else {
            newTerminals.push(updatedTerminal);
        }
        setTerminals(newTerminals);
    };

    // Apply filters whenever espelhos or filters change
    useEffect(() => {
        applyFilters();
    }, [espelhos, filters]);

    const applyFilters = () => {
        let filtered = espelhos;

        if (filters.label) {
            filtered = filtered.filter(espelho =>
                espelho.Label?.toLowerCase().includes(filters.label.toLowerCase())
            );
        }

        if (filters.connectionStatus) {
            filtered = filtered.filter(espelho =>
                espelho.ConnectionStatus === filters.connectionStatus
            );
        }

        setFilteredEspelhos(filtered);
    };

    const handleFilterChange = (name, value) => {
        setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
    };

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then(() => {
                    console.log('Conectado.');
                    setIsConnected(true);

                    connection.invoke("ConnectTech");

                    connection.on("ListenEspelhos", (data) => {
                        const espelhoJson = JSON.parse(data);
                        const sortedEspelhos = sortEspelhoJsonByLabel(espelhoJson);
                        setEspelhos(sortedEspelhos);
                    });

                    connection.on("ListenUpdateEspelho", (data) => {
                        const updatedEspelho = JSON.parse(data);
                        updateEspelho(updatedEspelho);
                    });

                    connection.on("ListenTerminals", (data) => {
                        const terminalsJson = JSON.parse(data);
                        setTerminals(terminalsJson);
                    });

                    connection.on("ListenTerminal", (data) => {
                        const updatedTerminal = JSON.parse(data);
                        updateTerminal(updatedTerminal);
                    });

                    connection.onclose((e) => {
                        console.log('CLOSE');
                        setIsConnected(false);
                    });

                    connection.onreconnected(() => {
                        console.log('RECONECTADO');
                        setIsConnected(true);
                    });

                })
                .catch((error) => {
                    setIsConnected(false);
                    console.log(error);
                });
        }
    }, [connection]);

    const handleOpenBrowser = (number) => {
        connection.invoke("SendOpenBrowser", number);
    };
    const handleCloseBrowser = (number) => {
        connection.invoke("SendCloseBrowser", number);
    };
    const handleRefreshBrowser = (number) => {
        connection.invoke("SendRefreshBrowser", number);
    };
    const handleGetComports = (number) => {
        connection.invoke("GetComPorts", number);
    };
    const handleSetComport = (number, portName) => {
        connection.invoke("SetComPort", number, portName);
    };
    const handleConnectComport = (number) => {
        connection.invoke("ConnectComPort", number);
    };
    const handleCloseComport = (number) => {
        connection.invoke("CloseComPort", number);
    };
    const handleSendDataToComport = (number, cmd) => {
        connection.invoke("SendDataToComPort", number, cmd);
    };

    return (
        <Container mt={-2}>
            {!isConnected &&
                <Box>
                    <Alert severity="warning">
                        Reconectando.
                    </Alert>
                </Box>
            }

            <ReturnContainer ml={-3} mb={2}>
                <IconButton aria-label="voltar" onClick={() => navigate(`/admin`)}>
                    <ArrowBackIosIcon />
                </IconButton>
                <Typography color="text.primary">Voltar</Typography>
            </ReturnContainer>
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" href="/admin">
                    Home
                </Link>
                <Link underline="hover" color="inherit" href="/admin/tecnico">
                    Técnico
                </Link>
                <Typography color="text.primary">Espelhos</Typography>
            </Breadcrumbs>

            <Header mt={3}>
                <Title>Espelhos</Title>
            </Header>

            <TerminalsResume espelhos={espelhos} />

            <EspelhoFilter handleFilterChange={handleFilterChange} filters={filters} />

            <EspelhoList
                espelhos={filteredEspelhos}
                terminals={terminals}
                handleOpenBrowser={handleOpenBrowser}
                handleCloseBrowser={handleCloseBrowser}
                handleRefreshBrowser={handleRefreshBrowser}
                handleGetComports={handleGetComports}
                handleSetComport={handleSetComport}
                handleConnectComport={handleConnectComport}
                handleCloseComport={handleCloseComport}
                handleSendDataToComport={handleSendDataToComport}
            />
        </Container>
    );
};

export default EspelhosView;
